import { Globe } from 'lucide-react';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DomainSearchInput } from './DomainSearchInput';
import { DomainPool } from './DomainPool';
import type { DomainSearchResult, SelectedDomain } from './types';

interface DomainSelectionStepProps {
  variant?: 'modal' | 'page';
  searchResults: DomainSearchResult[];
  selectedDomains: SelectedDomain[];
  isSearching: boolean;
  apiUnavailable: boolean;
  selectedTlds: string[];
  onSearch: (query: string) => void;
  onToggleDomain: (domain: DomainSearchResult) => void;
  onToggleTld: (tld: string) => void;
  onUnselectAll?: () => void;
  currencySymbol?: string;
}

const AVAILABLE_TLDS = [
  { value: 'com', label: '.com' },
  { value: 'org', label: '.org' },
];

export function DomainSelectionStep({
  variant = 'modal',
  searchResults,
  selectedDomains,
  isSearching,
  apiUnavailable,
  selectedTlds,
  onSearch,
  onToggleDomain,
  onToggleTld,
  onUnselectAll,
  currencySymbol = '$',
}: DomainSelectionStepProps) {
  const header = variant === 'modal' ? (
    <DialogHeader className="pt-0">
      <div className="flex items-center gap-3 mt-1">
        <Globe className="h-8 w-8 flex-shrink-0" />
        <div>
          <DialogTitle>Select Sender Domains</DialogTitle>
          <DialogDescription className="mt-1">
            Search and select domains for your email accounts
          </DialogDescription>
        </div>
      </div>
    </DialogHeader>
  ) : null;

  const content = (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <DomainSearchInput onSearch={onSearch} isSearching={isSearching} />
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          {AVAILABLE_TLDS.map((tld) => {
            const isChecked = selectedTlds.includes(tld.value);
            const isOnlyOneSelected = selectedTlds.length === 1 && isChecked;
            return (
              <label
                key={tld.value}
                className="flex items-center gap-0.5 cursor-pointer select-none"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => onToggleTld(tld.value)}
                  disabled={isOnlyOneSelected}
                />
                <span className="text-sm font-medium">{tld.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {apiUnavailable && (
        <p className="text-sm text-foreground">
          Domain search is unavailable at the moment. Please try again later.
        </p>
      )}

      {searchResults.length === 0 && !isSearching && !apiUnavailable && (
        <p className="text-sm text-foreground text-center py-8">
          Search for a domain name to get started
        </p>
      )}

      <DomainPool
        searchResults={searchResults}
        selectedDomains={selectedDomains}
        onToggleDomain={onToggleDomain}
        currencySymbol={currencySymbol}
      />
    </div>
  );

  if (variant === 'page') {
    return (
      <Card className="p-6 h-fit">
        {content}
        {selectedDomains.length > 0 && onUnselectAll && (
          <div className="flex justify-end mt-6">
            <Button variant="secondary" size="sm" onClick={onUnselectAll}>
              Unselect All
            </Button>
          </div>
        )}
      </Card>
    );
  }

  return (
    <>
      {header}
      <div className="pt-6 pb-2">{content}</div>
    </>
  );
}
